import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2, RefreshCw, Radio } from 'lucide-react';
import { inr } from './IsaKpis';

const at = v => (v ? new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '');

const Cell = ({ label, value, sub, cls = 'text-slate-900', testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
    <div className={`text-lg font-bold ${cls}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </div>
);

const Penalty = ({ label, amount }) => (
  <div className="flex justify-between text-xs"><span className="text-slate-600">{label}</span>
    <span className={amount ? 'text-rose-600 font-semibold' : 'text-slate-400'}>{amount ? inr(-amount) : '—'}</span></div>
);

export const IsaTodayCard = ({ live, loading, onLoad, rules }) => {
  const c = live?.calc;
  const need = c ? Math.max(0, rules.target - c.team_hot) : 0;
  return (
    <Card className="p-5 border-2 border-dashed border-amber-200 bg-amber-50/30" data-testid="isa-live">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
            <Radio className="w-4 h-4 text-rose-500" />Today so far
            <span className="text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Live · not final</span>
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {live ? `Fetched from Zoho CRM at ${at(live.as_of)} IST. ` : 'Click Update now to read the latest numbers from Zoho CRM. '}
            Nothing is saved — the final figures come from the 11:59 PM IST snapshot.</p>
        </div>
        <Button size="sm" onClick={onLoad} disabled={loading} data-testid="isa-live-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}Update now</Button>
      </div>
      {live && (
        <div className="mt-4 grid md:grid-cols-3 gap-5" data-testid="isa-live-values">
          <div className="space-y-3">
            <Cell label="Team Hot leads" value={`${c.team_hot} / ${rules.target}`} cls={c.target_met ? 'text-emerald-600' : 'text-slate-900'}
              sub={c.target_met ? 'Target reached' : `${need} more to reach the target`} testid="isa-live-team-hot" />
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${c.target_met ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(100, (c.team_hot * 100) / rules.target)}%` }} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Cell label="Unable to Reach" value={live.team.utr} sub={`limit ${rules.utr_limit}`} cls={live.team.utr > rules.utr_limit ? 'text-rose-600' : 'text-slate-900'} testid="isa-live-utr" />
              <Cell label="Call Back" value={live.team.callback} sub={`limit ${rules.callback_limit}`} cls={live.team.callback > rules.callback_limit ? 'text-rose-600' : 'text-slate-900'} testid="isa-live-callback" />
            </div>
          </div>
          <div className="space-y-1.5" data-testid="isa-live-people">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">By person</div>
            {Object.entries(live.people).map(([k, p]) => (
              <div key={k} className="text-xs flex justify-between gap-2 border-b border-slate-100 pb-1" data-testid={`isa-live-person-${k}`}>
                <span className="font-semibold text-slate-800">{p.name}</span>
                <span className="text-slate-600">{p.hot} Hot · UTR {p.utr} · CB {p.callback}</span>
              </div>
            ))}
            {live.team.utr_other > 0 && (
              <div className="text-xs flex justify-between gap-2 border-b border-slate-100 pb-1 text-slate-500" data-testid="isa-live-utr-other">
                <span>Other / untagged</span><span>UTR {live.team.utr_other} (counted in team total)</span>
              </div>
            )}
          </div>
          <div className="space-y-1.5" data-testid="isa-live-money">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">If the day ended now</div>
            <div className="flex justify-between text-xs"><span className="text-slate-600">Incentive</span><span className={c.incentive ? 'text-emerald-700 font-semibold' : 'text-slate-400'}>{c.incentive ? inr(c.incentive) : '—'}</span></div>
            <Penalty label={`Below ${rules.target} Hot`} amount={c.low_target_penalty} />
            <Penalty label={`Unable to Reach above ${rules.utr_limit}`} amount={c.utr_penalty} />
            <Penalty label={`Call Back above ${rules.callback_limit}`} amount={c.callback_penalty} />
            <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-1.5"><span>Net</span><span className={c.net < 0 ? 'text-rose-600' : 'text-slate-900'}>{inr(c.net)}</span></div>
            <div className="text-[11px] text-slate-500">{Object.entries(live.people).map(([k, p]) => `${p.name} ${inr(c.shares[k])}`).join(' · ')}</div>
          </div>
        </div>
      )}
    </Card>
  );
};
